import { useState } from "react";
import { Empty, Icon, Spinner, cx } from "../../components/ui";
import { ALASAN_TERKUNCI, NAMA_RUBRIK, namaBulan, statusRaport } from "./logika";

// Daftar murid satu kelas: nama, ID, status Bulan 1–3, dan status raportnya (belum lengkap,
// siap terbit, sudah terbit, berubah sejak terbit). Murid yang raportnya tak bisa diisi di
// sini (menunggu kelas utama, diisi di kelas lain, cabang lain) tetap tampil — terkunci,
// dengan sebabnya.

export const STATUS_BULAN = {
  lengkap: { label: "lengkap", titik: "bg-green-500 border-green-500" },
  sebagian: { label: "sebagian", titik: "bg-amber-400 border-amber-400" },
  kosong: { label: "kosong", titik: "bg-transparent border-slate-300 dark:border-slate-600" }
};

const SARINGAN = [
  ["semua", "Semua"],
  ["belum", "Belum lengkap"],
  ["siap", "Siap terbit"],
  ["terbit", "Sudah terbit"]
];

export function LabelRaport({ m }) {
  const { status, berubah } = statusRaport(m);
  if (berubah) return <span className="text-[11px] font-bold text-amber-700 dark:text-amber-300">Berubah sejak terbit</span>;
  if (status === "terbit") {
    return (
      <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-green-600">
        <Icon name="verified" className="text-sm" />
        Sudah terbit
      </span>
    );
  }
  return status === "siap"
    ? <span className="text-[11px] font-bold text-primary dark:text-[#3987e5]">Siap terbit</span>
    : <span className="text-[11px] font-bold text-slate-400">Belum lengkap</span>;
}

function TitikBulan({ bulan, term }) {
  return (
    <span className="flex items-center gap-1.5">
      {bulan.map((b, i) => (
        <span
          key={b.ke}
          title={`Bulan ${b.ke} (${namaBulan(term.bulan[i])}): ${STATUS_BULAN[b.status].label}`}
          aria-label={`Bulan ${b.ke} ${STATUS_BULAN[b.status].label}`}
          className={cx("inline-block h-3 w-3 rounded-full border-2", STATUS_BULAN[b.status].titik)}
        />
      ))}
    </span>
  );
}

export default function DaftarMurid({ rincian, galat, onBukaMurid, onKembali }) {
  const [saring, setSaring] = useState("semua");

  const kembali = (
    <button
      type="button"
      onClick={onKembali}
      className="mb-4 inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-bold text-slate-500 hover:bg-slate-100 hover:text-primary dark:hover:bg-slate-800"
    >
      <Icon name="chevron_left" />
      Semua kelas
    </button>
  );

  if (galat) return <div>{kembali}<Empty label={galat} /></div>;
  if (!rincian) return <div>{kembali}<Spinner label="Memuat murid…" /></div>;

  const { kelas, term, murid } = rincian;
  const cocok = (m, k) => k === "semua" || (m.bisaDiisi && statusRaport(m).status === k);
  const tampil = murid.filter((m) => cocok(m, saring));

  return (
    <div>
      {kembali}
      <div className="anim-rise mb-5">
        <h1 className="text-2xl font-black tracking-tight md:text-3xl">{kelas.nama}</h1>
        <p className="mt-1 text-sm text-slate-500">
          {[kelas.program, kelas.level, kelas.sesi].filter(Boolean).join(" · ")} · {NAMA_RUBRIK[kelas.rubrik]} · {term.name}
        </p>
        {!term.bolehTulis ? (
          <p className="mt-3 flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300">
            <Icon name="lock" className="text-base" />
            Term ini sudah terkunci — isian hanya bisa dilihat. Minta team leader membuka kuncinya bila perlu memperbaiki.
          </p>
        ) : !term.bukaTerbit && (
          <p className="mt-3 flex items-start gap-2 rounded-xl bg-slate-100 p-3 text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            <Icon name="event" className="text-base" />
            Raport term ini bisa diterbitkan mulai Bulan 3 ({namaBulan(term.bulan[2])}). Sampai saat itu, isi nilai tiap bulan seperti biasa.
          </p>
        )}
      </div>

      <div className="anim-rise anim-d1 mb-4 flex flex-wrap items-center gap-2" role="group" aria-label="Saring murid">
        {SARINGAN.map(([k, label]) => {
          const n = murid.filter((m) => cocok(m, k)).length;
          return (
            <button
              key={k}
              type="button"
              aria-pressed={saring === k}
              onClick={() => setSaring(k)}
              className={cx(
                "lift-press rounded-full border px-3 py-1.5 text-sm font-bold",
                saring === k
                  ? "border-primary bg-primary text-white"
                  : "border-slate-200 bg-white text-slate-600 hover:border-primary/60 hover:text-primary dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
              )}
            >
              {label} <span className="opacity-75">{n}</span>
            </button>
          );
        })}
        <span className="ml-auto flex items-center gap-3 text-[11px] text-slate-500">
          {Object.entries(STATUS_BULAN).map(([k, s]) => (
            <span key={k} className="inline-flex items-center gap-1">
              <span className={cx("inline-block h-2.5 w-2.5 rounded-full border-2", s.titik)} />
              {s.label}
            </span>
          ))}
        </span>
      </div>

      {!tampil.length ? (
        <Empty label={murid.length ? "Tidak ada murid di saringan ini." : "Belum ada murid di kelas ini."} />
      ) : (
        <ul className="anim-rise anim-d2 divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
          {tampil.map((m) => {
            const isi = (
              <>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-black text-slate-900 dark:text-white">{m.nama}</span>
                  <span className="block truncate text-xs text-slate-500">{m.studentId}</span>
                  {!m.bisaDiisi && (
                    <span className="mt-1 flex items-start gap-1 text-xs text-amber-700 dark:text-amber-300">
                      <Icon name="lock" className="text-sm" />
                      {m.alasan === "kelas-utama-lain" && m.kelasUtama
                        ? `Raportnya diisi di kelas ${m.kelasUtama.nama}.`
                        : ALASAN_TERKUNCI[m.alasan] || "Belum bisa diisi."}
                    </span>
                  )}
                </span>
                <span className="flex shrink-0 flex-col items-end gap-1">
                  <TitikBulan bulan={m.bulan} term={term} />
                  {m.bisaDiisi && <LabelRaport m={m} />}
                </span>
                {m.bisaDiisi
                  ? <Icon name="chevron_right" className="text-slate-400" />
                  : <span aria-hidden="true" className="w-6 shrink-0" />}
              </>
            );
            return (
              <li key={m.studentId}>
                {m.bisaDiisi ? (
                  <button
                    type="button"
                    onClick={() => onBukaMurid(m.studentId)}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/40"
                  >
                    {isi}
                  </button>
                ) : (
                  <div className="flex items-center gap-3 px-4 py-3 opacity-80">{isi}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
